/** 빌드에 박혀 있는 basePath (`next.config.ts` 와 같아야 한다) */
export const BUILD_BASE_PATH = '/v2'

/**
 * 지금 이 페이지가 실제로 깔린 접두어
 *
 * @description
 * 접두어가 늘 `/v2` 인 것은 아니다. 스튜디오 브랜치 미리보기는 칸을 하나 더 끼워
 * `/makers-studio/b/web-6017/v2` 아래에 띄운다. 거기서 `/v2/...` 로 부르면 루트를
 * 가리켜 엉뚱한 곳으로 간다 — 그림은 안 보이고 API 는 404 다(실측).
 *
 * Next 는 자기가 아는 접두어를 `_next` 에셋 주소에 이미 붙여 두었다. 그 주소에서
 * `/_next/` 앞을 떼어 쓰면 어디에 올리든 맞는다 — 띄우는 쪽 설정을 건드릴 필요가 없다.
 *
 * ⚠️ 브라우저에서만 알 수 있다. 서버에서는 빌드 값을 쓴다.
 */
export const readRuntimeBasePath = (): string => {
  if (typeof document === 'undefined') return BUILD_BASE_PATH

  const element = document.querySelector(
    'script[src*="/_next/"], link[href*="/_next/"]'
  )
  const url =
    element?.getAttribute('src') ?? element?.getAttribute('href') ?? ''
  const marker = url.indexOf('/_next/')

  return marker > 0 ? url.slice(0, marker) : BUILD_BASE_PATH
}

/**
 * 빌드 접두어로 만들어 둔 주소를 지금 깔린 자리에 맞춘다.
 *
 * 접두어가 같으면(운영·로컬) 아무것도 바꾸지 않는다. 다를 때만 앞부분을 갈아 끼운다.
 */
export const withRuntimeBasePath = (url: string): string => {
  if (!url.startsWith(`${BUILD_BASE_PATH}/`)) return url

  const prefix = readRuntimeBasePath()
  if (prefix === BUILD_BASE_PATH) return url

  return `${prefix}${url.slice(BUILD_BASE_PATH.length)}`
}

/**
 * axios 요청 설정의 주소를 지금 깔린 자리에 맞춘다. 브라우저에서만 바꾼다.
 * 스튜디오 미리보기처럼 칸이 하나 더 끼면 그대로 두면 루트로 나가 404 다.
 */
export const fixRequestBasePath = <
  T extends { baseURL?: string; url?: string },
>(
  config: T
): T => {
  if (typeof window === 'undefined') return config

  const joined = `${config.baseURL ?? ''}${config.url ?? ''}`
  const fixed = withRuntimeBasePath(joined)
  if (fixed === joined) return config

  config.baseURL = ''
  config.url = fixed
  return config
}

/**
 * 우리 쪽 경로를 **지금 깔린 자리 기준의 절대 주소**로 만든다.
 *
 * @description
 * PG 처럼 바깥에서 되돌아오는 주소는 절대 주소여야 한다. 그런데 `NEXT_PUBLIC_WEB_SERVICE_URL`
 * 로 만들면 스튜디오 미리보기처럼 칸이 하나 더 끼는 곳에서 그 칸이 빠진 주소가 나와,
 * 결제를 마치고 **우리 브랜치가 아닌 곳으로** 돌아온다(실측: 스튜디오 루트로 가서 403).
 *
 * 지금 페이지가 실제로 깔린 접두어를 쓰면 어디에 올리든 맞는다.
 */
export const toRuntimeUrl = (path: string): string => {
  const withoutBase = path.startsWith(`${BUILD_BASE_PATH}/`)
    ? path.slice(BUILD_BASE_PATH.length)
    : path

  return `${window.location.origin}${readRuntimeBasePath()}${withoutBase}`
}
